import { useMutation, useQueryClient } from 'react-query';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { WhatsappTemplatesResponse } from '@bandido/shared/types';

type UpdateTemplateEnabledPayload = {
  templateId: string;
  enabled: boolean;
};

export const useUpdateTemplateEnabled = (agencyId?: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ templateId, enabled }: UpdateTemplateEnabledPayload) => {
      if (!agencyId) {
        return Promise.reject(new Error('Agency ID is required'));
      }
      return client.patch(`/whatsapp/templates/${agencyId}/${templateId}`, { enabled });
    },
    {
      onMutate: async ({ templateId, enabled }) => {
        if (!agencyId) return undefined;
        await queryClient.cancelQueries([QueryKeys.gupshupTemplates, agencyId]);

        const previous = queryClient.getQueryData<WhatsappTemplatesResponse>([
          QueryKeys.gupshupTemplates,
          agencyId,
        ]);

        if (previous) {
          queryClient.setQueryData<WhatsappTemplatesResponse>(
            [QueryKeys.gupshupTemplates, agencyId],
            {
              ...previous,
              templates: previous.templates.map((template) =>
                template.id === templateId ? { ...template, enabled } : template,
              ),
            },
          );
        }

        return { previous };
      },
      onError: (_error, _variables, context) => {
        if (!agencyId) return;
        if (context?.previous) {
          queryClient.setQueryData([QueryKeys.gupshupTemplates, agencyId], context.previous);
        }
      },
      onSettled: () => {
        if (!agencyId) return;
        queryClient.invalidateQueries([QueryKeys.gupshupTemplates, agencyId]);
      },
    },
  );
};
